/**
 * Subscription Service
 * 
 * API service layer for subscription-related operations.
 * 
 * ## Key Features
 * - Type-safe discriminated unions (ApiResult<T>)
 * - Unified subscription endpoint with server-side priority logic
 * - Automatic error handling via apiClient
 * - Tenant context injection via appRuntime
 * 
 * ## What's New vs web-portal
 * - Single endpoint replaces separate membership + pass calls
 * - Priority logic (membership > pass) handled server-side
 * - 50% fewer API calls for subscription data
 * 
 * @module subscription/api
 */

import { apiClient } from '~/lib/apiClient';
import { appRuntime } from '~/services/appRuntime';
import type { ApiResult } from '~/lib/apiClient/types';
import type {
  GetCurrentUserSubscriptionPayload,
  ApiAvailableSubscriptionsResponse,
  ApiPurchaseQuoteResponse,
  SubscriptionQuoteType
} from '../types';

/**
 * Subscription Service
 * 
 * Handles all subscription-related API operations
 */
export const subscriptionService = {
  /**
   * Get user's active subscription with priority handling
   * 
   * Returns the active subscription for a user:
   * - If user has both membership and pass, returns membership (priority)
   * - If user has only one, returns that one
   * - If user has neither, returns null
   * 
   * **Priority Logic:**
   * 1. Active membership (not ended)
   * 2. Active pass (valid date range + not expired/cancelled)
   * 3. null (no active subscription)
   * 
   * @param userId - User ID to fetch subscription for
   * @returns ApiResult with active subscription data
   * 
   * @example
   * ```typescript
   * // Get active subscription
   * const result = await subscriptionService.getActiveSubscription(123);
   * 
   * if (result.success && result.payload.subscription) {
   *   const sub = result.payload.subscription;
   *   console.log(`User has active ${sub.type}: ${sub.plan?.name}`);
   *   
   *   if (sub.type === 'membership') {
   *     console.log('Membership plan:', sub.plan);
   *   } else {
   *     console.log('Pass plan:', sub.plan);
   *   }
   * } else {
   *   console.log('User has no active subscription');
   * }
   * ```
   */
  async getActiveSubscription(userId: number): Promise<ApiResult<GetCurrentUserSubscriptionPayload>> {
    const tenantHandle = appRuntime.tenantHandle;
    const clubId = appRuntime.clubId;
    
    if (!clubId) {
      return { success: false, message: 'Club ID is required to fetch active subscription' } as ApiResult<GetCurrentUserSubscriptionPayload>;
    }
    
    return await apiClient.get<GetCurrentUserSubscriptionPayload>(
      // `holdings`: every row the member holds (subscription unification D12) — the gift
      // lock and the receipt's refresh path read it; `upcoming` is refused on this route.
      `/subscriptions/user/${userId}/club/${clubId}/active?include=plan,usage,holdings`,
      {
        tenantHandle: tenantHandle || undefined,
      }
    );
  },

  /**
   * Get available subscriptions for purchase
   * 
   * Returns all memberships and passes available for purchase at a club.
   * Uses the unified subscriptions endpoint for a single API call.
   * 
   * Filters to only include subscriptions with:
   * - visibility = 'public' (done server-side; unlisted plans are deep-link
   *   only and never appear here)
   * - the new-member limiter, evaluated against the caller's club tenure
   * 
   * Results are ordered by price (ascending) on the server.
   * 
   * Used by:
   * - Subscription purchase page
   * - Membership/Pass selection UI
   * 
   * @param clubId - Club ID to fetch subscriptions for
   * @returns ApiResult with available subscriptions
   * 
   * @example
   * ```typescript
   * // Get available subscriptions for purchase
   * const result = await subscriptionService.getAvailableSubscriptions(2);
   * 
   * if (result.success) {
   *   const { memberships, passes } = result.payload;
   *   console.log(`${memberships.length} memberships, ${passes.length} passes available`);
   * }
   * ```
   */
  async getAvailableSubscriptions(clubId: number): Promise<ApiResult<ApiAvailableSubscriptionsResponse['payload']>> {
    const tenantHandle = appRuntime.tenantHandle;
    
    return await apiClient.get<ApiAvailableSubscriptionsResponse['payload']>(
      `/subscriptions/club/${clubId}/available`,
      {
        tenantHandle: tenantHandle || undefined,
      }
    );
  },

  /**
   * Price a membership/pass purchase — the server-authored quote (D12).
   *
   * Two addresses, one body and one response shape. `asMember` picks between
   * them:
   *
   * - **false** → `POST /subscriptions/quote`, tenant-only, no bearer. The
   *   guest funnel prices before any account exists, and asking for a token
   *   there logs a dev warning on every re-quote (booking-quote precedent).
   * - **true** → `POST /subscriptions/quote/me`, bearer attached. Identical
   *   price; the difference is that a promo code is evaluated WITH the
   *   caller's identity, so a spent per-customer cap, a D18 new-customer rule
   *   or a missing enrollment surface here instead of at Pay.
   *
   * Deliberately takes NO userId and no price on either route. The server
   * strips both — on `/quote/me` the identity comes from the token — so
   * offering the fields here would only invite a caller to believe they do
   * something. The response's `lineItems` + `totals` are what the charge path
   * will invoice; render them, never re-derive tax or fees locally.
   */
  async quoteSubscriptionPurchase(
    payload: {
      clubId: number;
      subscriptionType: SubscriptionQuoteType;
      subscriptionId: number;
      /** Optional `?promo=` capture (7a) — a code, never an amount; the server prices it. */
      promoCode?: string;
      /** How many passes (gifting). Absent = 1. Never recipients — recipients are a purchase concern. */
      quantity?: number;
    },
    options: { asMember?: boolean } = {}
  ): Promise<ApiResult<ApiPurchaseQuoteResponse['payload']>> {
    const asMember = options.asMember === true;
    return apiClient.post<ApiPurchaseQuoteResponse['payload']>(
      asMember ? '/subscriptions/quote/me' : '/subscriptions/quote',
      payload,
      asMember ? {} : { skipAuth: true },
    );
  },
};
